/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// material
import Space from 'material/space/space';
import { SortableList } from '@insticator/insticator-ui';
import { SectionGroupV2 } from 'material/expandablesection/v2'
import { ReactComponent as LockIcon } from 'assets/icons/lock.svg'
// styles
import injectSheet from 'react-jss'
import styles from './products_styles'


/* ========== ~~~~~~~~~~ PRODUCT MANAGER: SITE SETUP : FEATURES ~~~~~~~~~~ ========== */
const Products = injectSheet(styles)((props) => {

  const { accountProducts, pendingAccountProducts } = useSelector((state) => state.account);

  const { showCommenting, showTrendingNow, showArticleSentiment, onChange, onFieldsChange, onProductOrderChange, products } = props

  const dispatch = useDispatch()

  const productOptions = useMemo(() => {
    const PRODUCTS_OPTIONS = {
      COMMENTING_V2: <Space justifyContent={'space-between'}>Commenting</Space>,
      TRENDING_NOW_V2: <Space justifyContent={'space-between'}>Trending Now</Space>,
      ARTICLE_SENTIMENT_V2: <Space justifyContent={'space-between'}>Article Sentiment</Space>
    }
    const orderedProducts = products.map(product => {
      return {
        value: product,
        text: PRODUCTS_OPTIONS[product],
      }
    })
    return orderedProducts.filter(product => accountProducts.includes(product.value))
  }, [accountProducts, products])


  const [indices, setIndices] = useState(productOptions.map(item => item.value));
  const [selected, setSelected] = useState(showCommenting ? ['COMMENTING_V2'] : [])

  useEffect(() => {
    setIndices(productOptions.map(item => item.value))
  }, [accountProducts, productOptions])


  useEffect(() => {
    if (showCommenting && !selected.includes('COMMENTING_V2')) setSelected([...selected, 'COMMENTING_V2'])
    if (!showCommenting && selected.includes('COMMENTING_V2')) setSelected([...selected].filter(product => product !== 'COMMENTING_V2'))
  }, [selected, showCommenting])

  useEffect(() => {
    if (showTrendingNow && !selected.includes('TRENDING_NOW_V2')) setSelected([...selected, 'TRENDING_NOW_V2'])
    if (!showTrendingNow && selected.includes('TRENDING_NOW_V2')) setSelected([...selected].filter(product => product !== 'TRENDING_NOW_V2'))
  }, [selected, showTrendingNow])

  useEffect(() => {
    if (showArticleSentiment && !selected.includes('ARTICLE_SENTIMENT_V2')) setSelected([...selected, 'ARTICLE_SENTIMENT_V2'])
    if (!showArticleSentiment && selected.includes('ARTICLE_SENTIMENT_V2')) setSelected([...selected].filter(product => product !== 'ARTICLE_SENTIMENT_V2'))
  }, [selected, showArticleSentiment])


  const handleToggle = (orders, value) => {
    let productsOrder = {}
    if (selected.includes(value)) {
      setSelected(selected.filter(i => i !== value))
      if (value === 'COMMENTING_V2') productsOrder = { ...productsOrder, showCommenting: false }
      if (value === 'TRENDING_NOW_V2') productsOrder = { ...productsOrder, showTrendingNow: false }
      if (value === 'ARTICLE_SENTIMENT_V2') productsOrder = { ...productsOrder, showArticleSentiment: false }
    } else {
      setSelected([...selected, value])
      if (value === 'COMMENTING_V2') productsOrder = { ...productsOrder, showCommenting: true }
      if (value === 'TRENDING_NOW_V2') productsOrder = { ...productsOrder, showTrendingNow: true }
      if (value === 'ARTICLE_SENTIMENT_V2') productsOrder = { ...productsOrder, showArticleSentiment: true }
    }

    orders.forEach((order, index) => {
      const newOrder = index + 1
      if (order === 'COMMENTING_V2') productsOrder = { ...productsOrder, commentingOrder: newOrder }
      if (order === 'TRENDING_NOW_V2') productsOrder = { ...productsOrder, trendingNowOrder: newOrder }
      if (order === 'ARTICLE_SENTIMENT_V2') productsOrder = { ...productsOrder, articleSentimentOrder: newOrder }
    })
    onFieldsChange(productsOrder)
  }

  const handleOrderChange = (orders) => {
    setIndices(orders)
    let productsOrder = {}
    orders.forEach((order, index) => {
      const newOrder = index + 1
      if (order === 'COMMENTING_V2') productsOrder = { ...productsOrder, commentingOrder: newOrder }
      if (order === 'TRENDING_NOW_V2') productsOrder = { ...productsOrder, trendingNowOrder: newOrder }
      if (order === 'ARTICLE_SENTIMENT_V2') productsOrder = { ...productsOrder, articleSentimentOrder: newOrder }
    })
    onFieldsChange(productsOrder)
    onProductOrderChange?.(orders)
  }

  return (
    <SectionGroupV2
      klass={`products ${props.classes.products}`}
      title={'Products'}
      description={'Enable and sort products you’d like to group together on your site.'}
      direction={'column'}
    >
      <SortableList
        items={productOptions}
        error={{ "enabled": false, "message": "This is an error!" }}
        order={indices || []}
        selected={selected || []}
        type={'switch'}
        handleChange={(order, value) => handleToggle(order, value)}
        handleOrderChange={(order, value) => handleOrderChange(order, value)}
      />
      {!accountProducts.includes('COMMENTING_V2') && !pendingAccountProducts.includes('COMMENTING_V2') && (
        <Space klass={`productitem`} justifyContent={'space-between'}>
          Commenting
        </Space>
      )}
      {!accountProducts.includes('TRENDING_NOW_V2') && !pendingAccountProducts.includes('TRENDING_NOW_V2') && (
        <Space klass={`productitem`} justifyContent={'space-between'}>
          Trending Now
        </Space>
      )}
      {!accountProducts.includes('ARTICLE_SENTIMENT_V2') && !pendingAccountProducts.includes('ARTICLE_SENTIMENT_V2') && (
        <Space klass={`productitem`} justifyContent={'space-between'}>
          Article Sentiment
        </Space>
      )}
      {selected.length === 0 && <span className='errormessage'>Please select atleast 1 product.</span>}
    </SectionGroupV2>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default Products;